/**
 * The microphone.
 *
 * PadLab treats a recording as the most valuable thing in the app and the microphone as
 * the most dangerous permission it holds, so this module is built around four rules that
 * are not negotiable:
 *
 * 1. **`getUserMedia` is only ever called from a user gesture.** Nothing here touches the
 *    microphone at load, on navigation, or "to warm up". `requestAccess()` and `arm()` are
 *    the only doors, and both are called from a tap.
 * 2. **Every exit path stops every track.** Finish, cancel, error, device removal, page
 *    hide, disposal — all of them run through `releaseDevice()`. The recording light must
 *    never be on when PadLab is not recording.
 * 3. **`isCapturing` is true only while frames are genuinely arriving.** It is false while
 *    counting in, false while paused, and false the instant the encoder stops. The banner,
 *    the pad badge and the accessibility announcement all read that one signal, so the UI
 *    cannot claim to be recording when it is not.
 * 4. **Monitoring is off until the user turns it on, and turns itself off when it
 *    howls.** See `setMonitoring`.
 *
 * The output format is negotiated at runtime rather than assumed, and *proved* rather than
 * negotiated. `isTypeSupported` answers "will you write this", which is not the question:
 * WebKit answers yes to `audio/mp4` and then writes a fragmented stream its own
 * `decodeAudioData` refuses, so a take recorded on the primary target platform decoded to
 * nothing and was thrown away. `arm()` therefore records a fifth of a second through each
 * candidate and decodes it back, and only a container that survives the round trip is used.
 * Anything that fails is remembered so the probe is paid for once per browser. When nothing
 * survives — or `MediaRecorder` is missing altogether — the recorder falls back to an
 * `AudioWorklet` that ships Float32 blocks to a worker which writes a real 24-bit WAV: not a
 * placeholder, the same file the exporter would write.
 *
 * A fifth rule joins the four above: **captured audio reaches durable storage before it
 * reaches the user.** Every chunk is appended to a blob writer as it arrives, so a take
 * survives the tab discard, the phone call and the crash that ends the session.
 *
 * The public surface is deliberately shaped to be assignable to the app's `RecorderPort`,
 * so this can be dropped in wherever that port is consumed with no adapter.
 */

import { createLogger, tag } from '../core/log';
import { computed, signal, type ReadonlySignal, type Signal } from '../core/signal';
import { clamp, newUUID, nowISODate, type ISODate, type UUID } from '../model/types';
import { Metronome, WorkerClock } from './transport';
import { buildEffectChain, type EffectChainGraph } from './effects';
import { effectChainActiveNodes, effectChainSupportsLiveMonitoring, type EffectChain } from '../model/types';
import { setAudioSessionType } from '../platform/audio-session';

const log = createLogger('recorder');

// MARK: - Public types

export type RecorderPermission = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unsupported';

export interface StereoLevel {
  peak: number;
  rms: number;
}

const SILENT_LEVEL: StereoLevel = { peak: 0, rms: 0 };

export interface RecordedTake {
  id: UUID;
  name: string;
  blob: Blob;
  buffer: AudioBuffer;
  duration: number;
  createdAt: ISODate;
  mimeType: string;
  trimStart: number;
  trimEnd: number;
  /**
   * Row in the recordings store holding this take's bytes on disk. Present whenever the
   * vault accepted the capture; the app clears the row once the take is saved into the
   * library, and until then a reload can offer it back.
   */
  recoveryID?: string;
}

export interface StartRecordingOptions {
  /** Beats of count-in before capture begins. 0 starts immediately. */
  countInBeats?: number;
  /** Only used to time the count-in. */
  tempo?: number;
  beatsPerBar?: number;
  /** 0 silences the count-in clicks without removing the countdown. */
  metronomeVolume?: number;
}

export interface MonitoringOptions {
  /**
   * The user has said, in a dialog that named the risk, that they are wearing headphones.
   * Monitoring will not turn on without it. There is no browser API that can answer this
   * question, so the answer has to come from the person in the room.
   */
  headphonesConfirmed?: boolean;
}

/** The host audio graph. An `AudioPort` satisfies this shape as it stands. */
export interface RecorderHost {
  context(): BaseAudioContext | null;
  now(): number;
  unlock(): Promise<boolean>;
  /** Optional: when present, count-in clicks go through the app's own metronome bus. */
  click?(accented: boolean, when: number, volume: number): void;
}

/**
 * Durable storage for a take *while it is being captured*.
 *
 * Declared structurally so the recorder keeps no dependency on the storage layer; the app
 * supplies an implementation over the blob store (`storage/takes.ts`). Absent, the recorder
 * still works — it simply has no safety net if the tab is discarded mid-take.
 */
export interface RecordingSink {
  readonly id: string;
  /** Fire-and-forget; the implementation serialises writes. */
  append(chunk: Blob): void;
  finish(duration: number): Promise<number>;
  discard(): Promise<void>;
}

export interface RecordingVault {
  open(meta: {
    mimeType: string;
    fileExtension: string;
    sampleRate: number;
    channelCount: number;
  }): Promise<RecordingSink | null>;
  /** Keeps audio that could not become an editable take, so it is exportable rather than lost. */
  park(blob: Blob, meta: { mimeType: string; fileExtension: string; duration: number }): Promise<void>;
}

export interface RecorderOptions {
  host: RecorderHost;
  /** Where monitoring is sent. Defaults to the context destination. */
  monitorDestination?: AudioNode;
  /** Where audio goes as it is captured, and when it cannot become a take. */
  vault?: RecordingVault;
}

/**
 * Container types tried in order.
 *
 * MP4/AAC first because it is the only one every Apple platform can also *play* natively,
 * then Opus in WebM, then Opus in Ogg. **Order is a preference, not a decision** — see
 * `provenRecordingMimeType`. WebKit's `MediaRecorder` advertises `audio/mp4` and writes a
 * fragmented stream its own `decodeAudioData` rejects, so a preference acted on without
 * proof destroyed every take on the primary target platform.
 */
export const RECORDING_MIME_CANDIDATES: readonly string[] = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

/**
 * Containers this browser has been *observed* to write and then decode, and those it has
 * been observed to write and then refuse.
 *
 * Cached across reloads because the answer is a property of the browser build, not of the
 * session, and the probe costs a fifth of a second of microphone time.
 */
const DECODE_MEMO_KEY = 'padlab.recording.containers';

interface ContainerMemo {
  good: string[];
  bad: string[];
}

function readContainerMemo(): ContainerMemo {
  try {
    const raw = globalThis.localStorage?.getItem(DECODE_MEMO_KEY);
    if (raw === null || raw === undefined) return { good: [], bad: [] };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { good: [], bad: [] };
    const record = parsed as Partial<ContainerMemo>;
    return {
      good: Array.isArray(record.good) ? record.good.filter((entry) => typeof entry === 'string') : [],
      bad: Array.isArray(record.bad) ? record.bad.filter((entry) => typeof entry === 'string') : [],
    };
  } catch {
    return { good: [], bad: [] };
  }
}

function writeContainerMemo(memo: ContainerMemo): void {
  try {
    globalThis.localStorage?.setItem(DECODE_MEMO_KEY, JSON.stringify(memo));
  } catch {
    // Private browsing. The in-memory copy still holds for this session.
  }
}

let containerMemo: ContainerMemo | null = null;

function memo(): ContainerMemo {
  containerMemo ??= readContainerMemo();
  return containerMemo;
}

/** Records that `mimeType` produced audio this engine could decode. */
export function noteContainerDecoded(mimeType: string): void {
  const current = memo();
  if (current.good.includes(mimeType)) return;
  containerMemo = { good: [...current.good, mimeType], bad: current.bad.filter((entry) => entry !== mimeType) };
  writeContainerMemo(containerMemo);
}

/** Records that `mimeType` produced audio this engine could not decode. Never guessed. */
export function noteContainerUndecodable(mimeType: string): void {
  const current = memo();
  if (current.bad.includes(mimeType)) return;
  containerMemo = { good: current.good.filter((entry) => entry !== mimeType), bad: [...current.bad, mimeType] };
  writeContainerMemo(containerMemo);
}

/** Test seam: forgets everything learned about this browser's containers. */
export function resetContainerMemo(): void {
  containerMemo = { good: [], bad: [] };
  try {
    globalThis.localStorage?.removeItem(DECODE_MEMO_KEY);
  } catch {
    // Nothing to remove.
  }
}

/** File extension for a negotiated MIME type. */
export function extensionForMimeType(mimeType: string): string {
  if (mimeType.startsWith('audio/mp4')) return 'm4a';
  if (mimeType.startsWith('audio/webm')) return 'webm';
  if (mimeType.startsWith('audio/ogg')) return 'ogg';
  return 'wav';
}

/** Every candidate this browser will actually write, best first. */
export function supportedRecordingMimeTypes(): string[] {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return [];
  }
  return RECORDING_MIME_CANDIDATES.filter((candidate) => MediaRecorder.isTypeSupported(candidate));
}

/**
 * The containers still worth trying, best first: everything this browser will write, minus
 * everything it has already been caught writing and then failing to decode, with anything
 * proven good promoted to the front.
 */
export function candidateRecordingMimeTypes(): string[] {
  const { good, bad } = memo();
  const supported = supportedRecordingMimeTypes().filter((candidate) => !bad.includes(candidate));
  const proven = supported.filter((candidate) => good.includes(candidate));
  return [...proven, ...supported.filter((candidate) => !proven.includes(candidate))];
}

/** The type this browser will be asked to write, or `null` when `MediaRecorder` cannot. */
export function negotiateRecordingMimeType(): string | null {
  return candidateRecordingMimeTypes()[0] ?? null;
}

/**
 * How long the container probe records for. Long enough that every encoder emits a usable
 * header plus at least one frame, short enough to be invisible behind the permission grant.
 */
const CONTAINER_PROBE_MS = 220;

/**
 * Finds a container this browser both writes *and* reads, by writing one and reading it.
 *
 * Returns `null` when nothing survives the round trip, which is the signal to use the WAV
 * worklet instead. Runs against the already-open metering stream, so it costs no extra
 * permission prompt and no extra device open.
 */
export async function proveRecordingMimeType(
  stream: MediaStream,
  context: BaseAudioContext,
): Promise<string | null> {
  for (const candidate of candidateRecordingMimeTypes()) {
    if (memo().good.includes(candidate)) return candidate;
    let blob: Blob;
    try {
      blob = await recordProbe(stream, candidate);
    } catch {
      noteContainerUndecodable(candidate);
      continue;
    }
    if (blob.size === 0) {
      noteContainerUndecodable(candidate);
      continue;
    }
    try {
      await context.decodeAudioData(await blob.arrayBuffer());
      noteContainerDecoded(candidate);
      return candidate;
    } catch {
      // Written happily, unreadable by the same engine. This is the WebKit MP4 case.
      log.warn('recording container rejected by the decoder', { container: tag(candidate) });
      noteContainerUndecodable(candidate);
    }
  }
  return null;
}

function recordProbe(stream: MediaStream, mimeType: string): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType });
    } catch (error) {
      reject(error instanceof Error ? error : new Error('MediaRecorder refused this container.'));
      return;
    }
    const parts: Blob[] = [];
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(new Blob(parts, { type: mimeType }));
    };
    recorder.ondataavailable = (event): void => {
      if (event.data.size > 0) parts.push(event.data);
    };
    recorder.onstop = finish;
    recorder.onerror = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(new Error('The encoder failed while probing this container.'));
    };
    const timer = setTimeout(() => {
      try {
        if (recorder.state !== 'inactive') recorder.stop();
        else finish();
      } catch {
        finish();
      }
    }, CONTAINER_PROBE_MS);
    try {
      recorder.start();
    } catch (error) {
      settled = true;
      clearTimeout(timer);
      reject(error instanceof Error ? error : new Error('MediaRecorder refused to start.'));
    }
  });
}

// MARK: - Microphone constraints
//
// Every piece of "helpful" processing is turned off. Echo cancellation ducks the input
// whenever the app makes a sound, noise suppression eats the tail of a word, and automatic
// gain control rides the level up between phrases — all three are designed for a call and
// all three ruin a take. Mono because the input is one microphone; a fake stereo pair
// doubles the file for no information.

const MICROPHONE_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: 1,
  },
  video: false,
};

// MARK: - Worklet and worker sources
//
// Both are compiled to strings and instantiated from blob URLs. `script-src`/`worker-src`
// in the page CSP include `blob:` exactly for this; it needs no build-system support and
// keeps the fallback in one readable file with the code that decides to use it.
//
// They are exported so the test suite can exercise the fallback end to end — loading the
// processor, feeding it a signal and decoding what the encoder wrote — without needing a
// microphone or a permission prompt in CI.

export const RECORDER_WORKLET_SOURCE = `class PadLabRecorderProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.capacity = 4096;
    this.buffer = new Float32Array(this.capacity);
    this.filled = 0;
    this.active = false;
    this.port.onmessage = (event) => {
      const message = event.data;
      if (message === null || typeof message !== 'object') return;
      if (message.type === 'active') this.active = message.value === true;
      else if (message.type === 'flush') {
        this.flush();
        // Posted after the chunk, and the port is FIFO, so the main thread can use this as
        // proof that every captured frame has left the audio thread.
        this.port.postMessage({ type: 'flushed' });
      }
    };
  }
  flush() {
    if (this.filled === 0) return;
    const chunk = this.buffer.slice(0, this.filled);
    this.filled = 0;
    this.port.postMessage(chunk, [chunk.buffer]);
  }
  process(inputs) {
    if (!this.active) return true;
    const input = inputs[0];
    if (input === undefined || input.length === 0) return true;
    const channel = input[0];
    if (channel === undefined) return true;
    for (let index = 0; index < channel.length; index++) {
      this.buffer[this.filled++] = channel[index];
      if (this.filled === this.capacity) this.flush();
    }
    return true;
  }
}
registerProcessor('padlab-recorder', PadLabRecorderProcessor);
`;

export const WAV_ENCODER_WORKER_SOURCE = `let chunks = [];
let frames = 0;
let rate = 48000;

function encode() {
  const bytesPerSample = 3;
  const dataBytes = frames * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  function text(offset, value) {
    for (let index = 0; index < value.length; index++) view.setUint8(offset + index, value.charCodeAt(index));
  }
  text(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 24, true);
  text(36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  let peak = 0;
  for (let index = 0; index < chunks.length; index++) {
    const chunk = chunks[index];
    for (let position = 0; position < chunk.length; position++) {
      let sample = chunk[position];
      if (sample > 1) sample = 1;
      else if (sample < -1) sample = -1;
      const magnitude = sample < 0 ? -sample : sample;
      if (magnitude > peak) peak = magnitude;
      let value = Math.round(sample * 8388608);
      if (value > 8388607) value = 8388607;
      else if (value < -8388608) value = -8388608;
      const unsigned = value < 0 ? value + 16777216 : value;
      view.setUint8(offset, unsigned & 255);
      view.setUint8(offset + 1, (unsigned >> 8) & 255);
      view.setUint8(offset + 2, (unsigned >> 16) & 255);
      offset += 3;
    }
  }
  return { buffer: buffer, peak: peak };
}

self.onmessage = function (event) {
  const message = event.data;
  if (message === null || typeof message !== 'object') return;
  if (message.type === 'reset') {
    chunks = [];
    frames = 0;
    rate = message.sampleRate > 0 ? message.sampleRate : 48000;
  } else if (message.type === 'chunk') {
    const data = new Float32Array(message.data);
    chunks.push(data);
    frames += data.length;
  } else if (message.type === 'finish') {
    const result = encode();
    const total = frames;
    chunks = [];
    frames = 0;
    self.postMessage({ type: 'done', buffer: result.buffer, frames: total, sampleRate: rate, peak: result.peak }, [result.buffer]);
  }
};
`;

function blobURL(source: string): string {
  return URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
}

// MARK: - Feedback detection tuning
//
// Monitoring through a phone speaker is an acoustic loop, and a loop with gain above unity
// runs away in well under a second. There is no way to ask a browser what the output route
// is, so the guard has to listen to the result instead of the routing:
//
// * **Runaway growth** — the input's short-term level rising by a third, repeatedly, while
//   already loud. That is the loop's gain being greater than one; ordinary speech and
//   singing do not do it three windows in a row.
// * **Sustained ceiling** — near full scale for half a second. Feedback pins the input;
//   almost nothing else does.
// * **Periodic self-similarity** — the envelope correlating with itself at the round-trip
//   delay. A howl is the same energy going round and round, which is exactly a peak in the
//   envelope's autocorrelation at a lag that is not zero.
//
// Any of the three mutes monitoring immediately, with the reason shown to the user.

const FEEDBACK_GROWTH_RATIO = 1.35;
const FEEDBACK_GROWTH_STRIKES = 3;
const FEEDBACK_GROWTH_FLOOR = 0.063; // -24 dBFS
const FEEDBACK_CEILING_LEVEL = 0.85;
const FEEDBACK_CEILING_FRAMES = 15; // half a second at 30 Hz
const FEEDBACK_CORRELATION = 0.9;
const FEEDBACK_CORRELATION_FLOOR = 0.126; // -18 dBFS
const ENVELOPE_HISTORY = 48;

/** Clip is latched for two seconds so a single sample over full scale cannot be missed. */
const CLIP_LATCH_MS = 2000;
const CLIP_THRESHOLD = 0.992;
const METER_INTERVAL_MS = 1000 / 30;

const MONITORING_WARNING =
  'Monitoring is off. It plays the microphone back through the output, so it can only be used with headphones. Through a speaker it will feed back.';

// MARK: - Recorder

export class PadLabRecorder {
  private readonly host: RecorderHost;
  private readonly monitorDestination: AudioNode | undefined;

  private readonly permissionSignal: Signal<RecorderPermission> = signal<RecorderPermission>('unknown');
  private readonly capturingSignal: Signal<boolean> = signal(false);
  private readonly pausedSignal: Signal<boolean> = signal(false);
  private readonly startingSignal: Signal<boolean> = signal(false);
  private readonly inputOpenSignal: Signal<boolean> = signal(false);
  private readonly countingInSignal: Signal<number> = signal(0);
  private readonly elapsedSignal: Signal<number> = signal(0);
  private readonly levelSignal: Signal<StereoLevel> = signal<StereoLevel>(SILENT_LEVEL);
  private readonly clipSignal: Signal<boolean> = signal(false);
  private readonly monitoringSignal: Signal<boolean> = signal(false);
  private readonly mimeSignal: Signal<string> = signal(negotiateRecordingMimeType() ?? 'audio/wav');
  private readonly takesSignal: Signal<readonly RecordedTake[]> = signal<readonly RecordedTake[]>([]);
  private readonly noticeSignal: Signal<string | null> = signal<string | null>(null);
  /**
   * One signal for "a take is in flight", so no screen has to reassemble it out of three and
   * get a different answer than the recorder does. Pause and Discard are gated on this: they
   * were gated on `isCapturing`, which pause itself sets to false, so pausing disabled both
   * of the controls that could have finished or thrown away the take.
   */
  private readonly openTakeSignal: ReadonlySignal<boolean> = computed(
    () => this.capturingSignal.value || this.pausedSignal.value || this.startingSignal.value,
  );

  private stream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyser: AnalyserNode | null = null;
  private analysisBuffer: Float32Array<ArrayBuffer> = new Float32Array(1024);
  private monitorGain: GainNode | null = null;
  private metronome: Metronome | null = null;

  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];

  private workletNode: AudioWorkletNode | null = null;
  private encoderWorker: Worker | null = null;
  private workletModuleURL: string | null = null;
  private encoderWorkerURL: string | null = null;
  private workletFrames = 0;
  private flushResolver: (() => void) | null = null;

  private readonly meterClock = new WorkerClock(METER_INTERVAL_MS);
  private meterRunning = false;
  private clipTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly envelope: number[] = [];
  private growthStrikes = 0;
  private ceilingFrames = 0;

  private countInTimer: ReturnType<typeof setTimeout> | null = null;
  private countInResolver: (() => void) | null = null;
  private countInCancelled = false;
  private headphonesConfirmed = false;
  /** A container proven by `arm()` to survive write-then-decode on this browser. */
  private provenMimeType: string | null = null;
  private readonly vault: RecordingVault | undefined;
  private sink: RecordingSink | null = null;
  private monitorChain: EffectChainGraph | null = null;
  private permissionWatched = false;
  private workletProducedBlob = false;
  private takeCounter = 0;
  private segmentStart = 0;
  private accumulatedElapsed = 0;
  private disposed = false;
  private readonly releaseOnHide = (): void => {
    // Leaving the page with the microphone open is the one failure this module exists to
    // prevent, so the take is finalised on the spot rather than waiting for a promise the
    // browser may never resume.
    if (this.inputOpenSignal.peek()) {
      this.noticeSignal.value = 'Recording stopped because PadLab left the foreground.';
      this.abort({ rescue: true });
    }
  };

  constructor(options: RecorderOptions) {
    this.host = options.host;
    this.monitorDestination = options.monitorDestination;
    this.vault = options.vault;
    if (typeof MediaRecorder === 'undefined' && !this.canUseWorkletFallback()) {
      this.permissionSignal.value = 'unsupported';
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.releaseOnHide);
      window.addEventListener('beforeunload', this.releaseOnHide);
    }
  }

  // MARK: Observable state

  get permission(): ReadonlySignal<RecorderPermission> {
    return this.permissionSignal;
  }

  /** True only while frames are genuinely arriving from the microphone. */
  get isCapturing(): ReadonlySignal<boolean> {
    return this.capturingSignal;
  }

  get isPaused(): ReadonlySignal<boolean> {
    return this.pausedSignal;
  }

  /**
   * True from the moment `start()` is entered until frames are arriving — the permission
   * round trip and the count-in.
   *
   * The record button reads it so its name flips to "Stop recording" as soon as it is
   * pressed. Until this existed the button was called "Record" for the whole count-in and
   * pressing it again re-armed the recorder, which is the worst pair of behaviours it could
   * have had for a user who cannot see the red dot.
   */
  get isStarting(): ReadonlySignal<boolean> {
    return this.startingSignal;
  }

  /** True whenever a take is open: counting in, capturing, or paused mid-take. */
  get hasOpenTake(): ReadonlySignal<boolean> {
    return this.openTakeSignal;
  }

  /** True whenever the microphone is open, including while merely armed for metering. */
  get isInputOpen(): ReadonlySignal<boolean> {
    return this.inputOpenSignal;
  }

  /** Beats left in the count-in; 0 when there is not one running. */
  get countInBeatsRemaining(): ReadonlySignal<number> {
    return this.countingInSignal;
  }

  get elapsed(): ReadonlySignal<number> {
    return this.elapsedSignal;
  }

  get inputLevel(): ReadonlySignal<StereoLevel> {
    return this.levelSignal;
  }

  get didClip(): ReadonlySignal<boolean> {
    return this.clipSignal;
  }

  get isMonitoring(): ReadonlySignal<boolean> {
    return this.monitoringSignal;
  }

  get mimeType(): ReadonlySignal<string> {
    return this.mimeSignal;
  }

  /** Every take of this session, oldest first. A retake never destroys the one before it. */
  get takes(): ReadonlySignal<readonly RecordedTake[]> {
    return this.takesSignal;
  }

  /** The last thing the recorder needs the user to know, or `null`. */
  get notice(): ReadonlySignal<string | null> {
    return this.noticeSignal;
  }

  supportedMimeTypes(): readonly string[] {
    const negotiated = supportedRecordingMimeTypes();
    return negotiated.length > 0 ? negotiated : this.canUseWorkletFallback() ? ['audio/wav'] : [];
  }

  /** True when this browser can record at all — by either route. */
  get isSupported(): boolean {
    if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) return false;
    return supportedRecordingMimeTypes().length > 0 || this.canUseWorkletFallback();
  }

  private canUseWorkletFallback(): boolean {
    const context = this.host.context();
    return context !== null && 'audioWorklet' in context && typeof Worker !== 'undefined';
  }

  // MARK: Permission

  /**
   * Presents the system prompt. Only ever called from a user action, and it releases the
   * device again immediately: permission is a persistent grant, so there is no reason to
   * hold the microphone open between the moment the user agrees and the moment they record.
   */
  async requestAccess(): Promise<RecorderPermission> {
    if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
      this.permissionSignal.value = 'unsupported';
      this.noticeSignal.value =
        'This browser will not give PadLab access to a microphone. Open PadLab in Safari or Chrome over HTTPS to record.';
      return 'unsupported';
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia(MICROPHONE_CONSTRAINTS);
      for (const track of stream.getTracks()) track.stop();
      this.permissionSignal.value = 'granted';
      this.noticeSignal.value = null;
      return 'granted';
    } catch (error) {
      const permission = this.classifyPermissionError(error);
      this.permissionSignal.value = permission;
      return permission;
    }
  }

  /** Reads the stored decision without prompting, where the browser exposes it. */
  async peekPermission(): Promise<RecorderPermission> {
    if (typeof navigator === 'undefined' || navigator.permissions?.query === undefined) {
      return this.permissionSignal.peek();
    }
    try {
      // Safari has no 'microphone' descriptor and throws; the catch is the Safari path.
      const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      const mapped: RecorderPermission =
        status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'prompt';
      this.permissionSignal.value = mapped;
      // Subscribed once, and only where the browser supports it. Without this, a permission
      // revoked in another tab or in system settings while PadLab is open went unnoticed
      // until the next attempt to record — which is the worst possible moment to find out.
      if (!this.permissionWatched) {
        this.permissionWatched = true;
        status.onchange = (): void => {
          const next: RecorderPermission =
            status.state === 'granted' ? 'granted' : status.state === 'denied' ? 'denied' : 'prompt';
          this.permissionSignal.value = next;
          if (next !== 'granted' && this.inputOpenSignal.peek()) {
            this.noticeSignal.value = 'Microphone access was withdrawn, so recording stopped.';
            this.abort({ rescue: true });
          }
        };
      }
      return mapped;
    } catch {
      return this.permissionSignal.peek();
    }
  }

  private classifyPermissionError(error: unknown): RecorderPermission {
    const name = error instanceof Error ? error.name : '';
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      this.noticeSignal.value =
        'PadLab does not have permission to use the microphone. Allow it in your browser’s site settings for this page, then try again.';
      return 'denied';
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError') {
      this.noticeSignal.value =
        'No microphone was found. Connect one, or check that another app is not holding it, then try again.';
      return 'prompt';
    }
    this.noticeSignal.value = 'The microphone could not be opened. Close any other app using it, then try again.';
    log.error('getUserMedia failed', error);
    return 'prompt';
  }

  // MARK: Device lifecycle

  /**
   * Opens the microphone for live metering without recording anything. The input light is
   * on from this moment, which is why `isInputOpen` exists and the UI shows it.
   */
  async arm(): Promise<boolean> {
    if (this.disposed) return false;
    if (this.stream !== null) return true;
    if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia === undefined) {
      this.permissionSignal.value = 'unsupported';
      return false;
    }

    await this.host.unlock();
    const context = this.host.context();
    if (context === null || !isRealtimeContext(context)) {
      this.noticeSignal.value = 'Audio is not running yet. Tap anywhere in PadLab, then try again.';
      return false;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(MICROPHONE_CONSTRAINTS);
    } catch (error) {
      this.permissionSignal.value = this.classifyPermissionError(error);
      return false;
    }

    // `playback` cannot capture. The category has to widen for as long as the microphone is
    // open, and is handed straight back in `releaseDevice` because `play-and-record` costs
    // output level and can route to the receiver on some devices.
    setAudioSessionType('play-and-record');

    this.permissionSignal.value = 'granted';
    this.stream = stream;
    this.inputOpenSignal.value = true;
    this.noticeSignal.value = null;

    for (const track of stream.getTracks()) {
      track.addEventListener('ended', () => {
        // The device went away mid-take (unplugged, or another app claimed it).
        if (this.capturingSignal.peek()) {
          this.noticeSignal.value = 'The microphone was disconnected, so recording stopped.';
          void this.stop();
        } else {
          this.releaseDevice();
        }
      });
    }

    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    // No smoothing: the meter has to show a transient, and clip detection has to see the
    // one sample that went over rather than an average that hid it.
    analyser.smoothingTimeConstant = 0;
    this.analysisBuffer = new Float32Array(analyser.fftSize);
    source.connect(analyser);

    const monitor = context.createGain();
    monitor.gain.value = 0;
    analyser.connect(monitor);
    monitor.connect(this.monitorDestination ?? context.destination);

    this.sourceNode = source;
    this.analyser = analyser;
    this.monitorGain = monitor;
    this.startMetering();

    // Settle the container question while the user is still reading the screen, not after
    // they have performed a take. `arm()` resolves either way: a browser with no decodable
    // container records through the WAV worklet instead, which is slower to encode and
    // entirely reliable.
    if (this.provenMimeType === null && typeof MediaRecorder !== 'undefined') {
      try {
        this.provenMimeType = await proveRecordingMimeType(stream, context);
        this.mimeSignal.value = this.provenMimeType ?? 'audio/wav';
      } catch (error) {
        log.warn('container probe failed', { reason: tag(error instanceof Error ? error.name : 'unknown') });
        this.provenMimeType = null;
        this.mimeSignal.value = 'audio/wav';
      }
    }
    return true;
  }

  /** Stops every track and tears the graph down. Safe to call at any time, repeatedly. */
  releaseDevice(): void {
    this.stopMetering();
    this.monitoringSignal.value = false;
    this.headphonesConfirmed = false;

    if (this.monitorGain !== null) {
      this.monitorGain.gain.value = 0;
      this.monitorGain.disconnect();
      this.monitorGain = null;
    }
    this.analyser?.disconnect();
    this.analyser = null;
    this.monitorChain?.dispose();
    this.monitorChain = null;
    this.workletNode?.port.postMessage({ type: 'active', value: false });
    this.workletNode?.disconnect();
    this.workletNode = null;
    this.sourceNode?.disconnect();
    this.sourceNode = null;

    if (this.stream !== null) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
    // Back to the instrument category the moment the microphone is gone, so pads regain full
    // output level and keep playing through the Ring/Silent switch.
    setAudioSessionType('playback');
    this.inputOpenSignal.value = false;
    this.levelSignal.value = SILENT_LEVEL;
  }

  // MARK: Recording

  /**
   * Starts a take. Resolves once frames are genuinely being captured — after the count-in,
   * not before it — so a caller can `await` it and know the recording is live.
   */
  async start(options: StartRecordingOptions = {}): Promise<void> {
    if (this.disposed) throw new Error('This recorder has been disposed.');
    if (this.capturingSignal.peek()) return;
    // A take that exists is never thrown away by a second press of the button that made it.
    // `capturing` alone was not enough: it is false while paused and false during the
    // count-in, so pressing record again in either state re-entered here, re-armed the
    // device and reached `startMediaRecorder`, whose first statement is `this.chunks = []`.
    // Pausing and pressing record destroyed the paused audio; pressing twice during a
    // count-in left a second `MediaRecorder` running against the same stream.
    if (this.pausedSignal.peek()) {
      this.resume();
      return;
    }
    if (this.startingSignal.peek()) return;
    if (!this.isSupported) {
      throw new Error(
        'This browser cannot record audio. Open PadLab in Safari or Chrome over HTTPS, or import a sound from Files instead.',
      );
    }
    this.startingSignal.value = true;
    try {
      if (!(await this.arm())) {
        throw new Error(
          this.noticeSignal.peek() ??
            'PadLab could not open the microphone. Check the permission for this page, then try again.',
        );
      }

      this.accumulatedElapsed = 0;
      this.elapsedSignal.value = 0;
      this.clearClip();
      this.countInCancelled = false;

      const countInBeats = Math.max(0, Math.round(options.countInBeats ?? 0));
      if (countInBeats > 0) {
        await this.runCountIn(countInBeats, options);
        if (this.countInCancelled) return;
      }

      const stream = this.stream;
      if (stream === null) throw new Error('The microphone closed before recording began. Try again.');

      // `provenMimeType` is set by `arm()` from a real write-then-decode round trip. Only a
      // container that survived it is used; anything else records as WAV through the
      // worklet, which every browser with an `AudioWorklet` can read back.
      const negotiated = this.provenMimeType;
      await this.openSink(negotiated ?? 'audio/wav');
      if (negotiated !== null) {
        this.mimeSignal.value = negotiated;
        this.startMediaRecorder(stream, negotiated);
      } else if (this.canUseWorkletFallback()) {
        this.mimeSignal.value = 'audio/wav';
        await this.startWorkletRecorder();
      } else {
        throw new Error(
          'This browser records audio PadLab cannot read back, and it has no worklet to fall back on. Import a sound from Files instead.',
        );
      }

      this.segmentStart = this.host.now();
      this.pausedSignal.value = false;
      this.capturingSignal.value = true;
    } finally {
      this.startingSignal.value = false;
    }
  }

  private startMediaRecorder(stream: MediaStream, mimeType: string): void {
    this.chunks = [];
    const recorder = new MediaRecorder(stream, { mimeType });
    recorder.ondataavailable = (event) => {
      if (event.data.size === 0) return;
      this.chunks.push(event.data);
      // Straight to disk as well as to memory. This is the line that decides whether a phone
      // call taken mid-take costs the user their recording.
      this.sink?.append(event.data);
    };
    recorder.onerror = () => {
      this.noticeSignal.value = 'Recording stopped because the encoder failed. The audio up to that point was kept.';
      this.capturingSignal.value = false;
    };
    this.mediaRecorder = recorder;
    // A timeslice means chunks arrive continuously, so an interrupted session still has
    // most of the take in memory instead of one buffer the browser never handed over.
    recorder.start(500);
  }

  /**
   * Opens the durable sink for the take about to start.
   *
   * A take from the WAV worklet arrives as one blob at the end, so its sink only receives a
   * single append — but it still gets a row, which is what makes a finished-but-unsaved take
   * survive a reload.
   */
  private async openSink(mimeType: string): Promise<void> {
    if (this.vault === undefined) return;
    const context = this.host.context();
    try {
      this.sink = await this.vault.open({
        mimeType,
        fileExtension: extensionForMimeType(mimeType),
        sampleRate: context?.sampleRate ?? 0,
        channelCount: 1,
      });
    } catch (error) {
      this.sink = null;
      log.warn('durable take storage unavailable', { reason: tag(error instanceof Error ? error.name : 'unknown') });
    }
  }

  private async startWorkletRecorder(): Promise<void> {
    const context = this.host.context();
    if (context === null || this.sourceNode === null) {
      throw new Error('Audio is not running, so recording cannot start. Tap anywhere in PadLab, then try again.');
    }

    if (this.workletModuleURL === null) {
      this.workletModuleURL = blobURL(RECORDER_WORKLET_SOURCE);
      await context.audioWorklet.addModule(this.workletModuleURL);
    }
    if (this.encoderWorker === null) {
      this.encoderWorkerURL = blobURL(WAV_ENCODER_WORKER_SOURCE);
      this.encoderWorker = new Worker(this.encoderWorkerURL);
    }

    this.workletFrames = 0;
    this.encoderWorker.postMessage({ type: 'reset', sampleRate: context.sampleRate });

    const node = new AudioWorkletNode(context, 'padlab-recorder', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
      channelCountMode: 'explicit',
    });
    node.port.onmessage = (event) => {
      const data = event.data as Float32Array | { type?: string };
      if (data instanceof Float32Array) {
        if (data.length === 0) return;
        this.workletFrames += data.length;
        this.encoderWorker?.postMessage({ type: 'chunk', data: data.buffer }, [data.buffer]);
        return;
      }
      if (data.type === 'flushed') {
        const resolve = this.flushResolver;
        this.flushResolver = null;
        resolve?.();
      }
    };
    this.sourceNode.connect(node);
    node.port.postMessage({ type: 'active', value: true });
    this.workletNode = node;
  }

  pause(): void {
    if (!this.capturingSignal.peek() || this.pausedSignal.peek()) return;
    this.accumulatedElapsed += Math.max(0, this.host.now() - this.segmentStart);
    this.pausedSignal.value = true;
    // Capture genuinely stops, so `isCapturing` has to say so.
    this.capturingSignal.value = false;
    if (this.mediaRecorder !== null && this.mediaRecorder.state === 'recording') this.mediaRecorder.pause();
    this.workletNode?.port.postMessage({ type: 'active', value: false });
  }

  resume(): void {
    if (!this.pausedSignal.peek()) return;
    this.segmentStart = this.host.now();
    this.pausedSignal.value = false;
    this.capturingSignal.value = true;
    if (this.mediaRecorder !== null && this.mediaRecorder.state === 'paused') this.mediaRecorder.resume();
    this.workletNode?.port.postMessage({ type: 'active', value: true });
  }

  /**
   * Ends the take, decodes it once so the editor has a waveform immediately, appends it to
   * `takes`, and releases the device. Returns `null` when nothing was captured.
   */
  async stop(): Promise<RecordedTake | null> {
    if (this.countInTimer !== null) {
      this.cancelCountIn();
      return null;
    }
    if (!this.capturingSignal.peek() && !this.pausedSignal.peek()) {
      this.releaseDevice();
      return null;
    }

    if (!this.pausedSignal.peek()) {
      this.accumulatedElapsed += Math.max(0, this.host.now() - this.segmentStart);
    }
    this.capturingSignal.value = false;
    this.pausedSignal.value = false;

    let blob: Blob | null = null;
    let mimeType = this.mimeSignal.peek();
    try {
      if (this.mediaRecorder !== null) {
        blob = await this.finishMediaRecorder();
        mimeType = this.mimeSignal.peek();
      } else if (this.workletNode !== null) {
        blob = await this.finishWorkletRecorder();
        mimeType = 'audio/wav';
        this.workletProducedBlob = true;
      }
    } finally {
      this.releaseDevice();
    }

    const elapsed = this.accumulatedElapsed;
    const sink = this.sink;
    this.sink = null;

    if (blob === null || blob.size === 0) {
      await sink?.discard().catch(() => undefined);
      this.noticeSignal.value = 'Nothing was captured. Check that the microphone is not muted, then try again.';
      return null;
    }

    // The worklet path produces its blob at the end rather than in chunks, so this is where
    // its bytes reach the sink. The MediaRecorder path has been appending all along and its
    // sink already holds them.
    if (sink !== null && this.workletProducedBlob) sink.append(blob);
    const recoveryID = sink === null ? undefined : sink.id;
    await sink?.finish(elapsed).catch(() => undefined);
    this.workletProducedBlob = false;

    const context = this.host.context();
    if (context === null) {
      this.noticeSignal.value =
        'Audio stopped before the take could be prepared. The recording is safe on this device. Reopen PadLab to pick it up.';
      return null;
    }

    let buffer: AudioBuffer;
    try {
      buffer = await context.decodeAudioData(await blob.arrayBuffer());
      noteContainerDecoded(mimeType);
    } catch (error) {
      // Written by this browser, unreadable by this browser. Say exactly that — the old
      // message blamed the microphone for a decoder fault — switch container for the next
      // take, and keep the bytes: they are still exportable even though they cannot be
      // edited here.
      log.error('take failed to decode', error);
      noteContainerUndecodable(mimeType);
      this.provenMimeType = null;
      if (sink === null) {
        await this.vault
          ?.park(blob, { mimeType, fileExtension: extensionForMimeType(mimeType), duration: elapsed })
          .catch(() => undefined);
      }
      this.noticeSignal.value =
        'This browser wrote the recording in a format it will not read back, so it could not be opened for editing. It has been kept on this device and can be exported. The next take will use a different format.';
      return null;
    }

    this.takeCounter += 1;
    const take: RecordedTake = {
      id: newUUID(),
      name: `Take ${this.takeCounter}`,
      blob,
      buffer,
      duration: buffer.duration,
      createdAt: nowISODate(),
      mimeType,
      trimStart: 0,
      trimEnd: buffer.duration,
      ...(recoveryID === undefined ? {} : { recoveryID }),
    };
    this.takesSignal.value = [...this.takesSignal.peek(), take];
    this.elapsedSignal.value = buffer.duration;
    return take;
  }

  private finishMediaRecorder(): Promise<Blob> {
    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    if (recorder === null) return Promise.resolve(new Blob([]));
    if (recorder.state === 'inactive') {
      return Promise.resolve(new Blob(this.chunks, { type: this.mimeSignal.peek() }));
    }
    return new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: this.mimeSignal.peek() }));
      recorder.stop();
    });
  }

  private async finishWorkletRecorder(): Promise<Blob> {
    const node = this.workletNode;
    const worker = this.encoderWorker;
    if (node === null || worker === null || this.workletFrames === 0) return new Blob([]);

    // The processor holds up to 4096 frames — 85 ms — that have not been posted yet. They
    // have to reach the encoder before it is told to finish, so the flush is *acknowledged*
    // rather than assumed: the acknowledgement travels the same FIFO port as the chunk, so
    // receiving it proves the chunk arrived first. The timeout is only there so a wedged
    // audio thread cannot hang the take for ever.
    await new Promise<void>((resolve) => {
      this.flushResolver = resolve;
      node.port.postMessage({ type: 'active', value: false });
      node.port.postMessage({ type: 'flush' });
      setTimeout(() => {
        if (this.flushResolver === resolve) {
          this.flushResolver = null;
          resolve();
        }
      }, 250);
    });

    return new Promise<Blob>((resolve) => {
      const timeout = setTimeout(() => {
        // The encoder is a plain loop over what it already holds; if it has not answered in
        // five seconds something is badly wrong, and a silent hang is worse than no file.
        worker.onmessage = null;
        resolve(new Blob([]));
      }, 5000);

      worker.onmessage = (event: MessageEvent) => {
        const message = event.data as { type?: string; buffer?: ArrayBuffer };
        if (message.type !== 'done' || message.buffer === undefined) return;
        clearTimeout(timeout);
        worker.onmessage = null;
        resolve(new Blob([message.buffer], { type: 'audio/wav' }));
      };
      worker.postMessage({ type: 'finish' });
    });
  }

  /** Aborts the take and throws it away — the user asked. The previous takes are untouched. */
  cancel(): void {
    this.cancelCountIn();
    this.abort({ rescue: false });
    this.elapsedSignal.value = 0;
  }

  /**
   * `rescue` distinguishes the two callers, which want opposite things.
   *
   * Discard means discard: the user pressed it, and leaving the audio on disk to be offered
   * back at the next launch would be its own kind of broken. A page-hide is the opposite —
   * nobody chose it, so whatever reached the disk stays there and the row is closed as an
   * interrupted recording for the launch after this one to find.
   */
  private abort(options: { rescue: boolean }): void {
    if (this.mediaRecorder !== null) {
      try {
        // `stop()` flushes one last `dataavailable` synchronously in every engine that
        // implements it, which is the difference between losing the final half-second and
        // keeping it.
        if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
      } catch {
        // A recorder that is already stopping is not an error.
      }
      this.mediaRecorder = null;
    }
    const sink = this.sink;
    this.sink = null;
    if (sink !== null) {
      if (options.rescue) void sink.finish(this.accumulatedElapsed).catch(() => undefined);
      else void sink.discard().catch(() => undefined);
    }
    this.chunks = [];
    this.workletFrames = 0;
    this.encoderWorker?.postMessage({ type: 'reset', sampleRate: this.host.context()?.sampleRate ?? 48000 });
    this.capturingSignal.value = false;
    this.pausedSignal.value = false;
    // Rule 2: every exit path leaves no state claiming a take is in flight. `start()` clears
    // this in its own `finally`, but a page-hide abort can land before that runs.
    this.startingSignal.value = false;
    this.releaseDevice();
  }

  discardTake(id: UUID): void {
    this.takesSignal.value = this.takesSignal.peek().filter((take) => take.id !== id);
  }

  clearTakes(): void {
    this.takesSignal.value = [];
  }

  // MARK: Count-in

  private async runCountIn(beats: number, options: StartRecordingOptions): Promise<void> {
    const context = this.host.context();
    if (context === null) return;
    const tempo = clamp(options.tempo ?? 96, 20, 300);
    const secondsPerBeat = 60 / tempo;
    const beatsPerBar = Math.max(1, Math.round(options.beatsPerBar ?? 4));
    const volume = clamp(options.metronomeVolume ?? 0.6, 0, 1);

    if (volume > 0) {
      const startTime = context.currentTime + 0.08;
      for (let beat = 0; beat < beats; beat++) {
        const at = startTime + beat * secondsPerBeat;
        const accent = beat % beatsPerBar === 0;
        if (this.host.click !== undefined) this.host.click(accent, at, volume);
        else this.clickMetronome().schedule(at, accent, volume);
      }
    }

    this.countingInSignal.value = beats;
    await new Promise<void>((resolve) => {
      // Held so `cancelCountIn` can settle it. Clearing the timer alone left this promise
      // pending for ever, so the `start()` that was awaiting it never returned and never ran
      // its `finally` — the recorder stayed "starting" until the page was reloaded.
      this.countInResolver = resolve;
      let remaining = beats;
      const step = (): void => {
        remaining -= 1;
        this.countingInSignal.value = Math.max(0, remaining);
        if (remaining <= 0 || this.countInCancelled) {
          this.countInTimer = null;
          this.countingInSignal.value = 0;
          this.countInResolver = null;
          resolve();
          return;
        }
        this.countInTimer = setTimeout(step, secondsPerBeat * 1000);
      };
      this.countInTimer = setTimeout(step, secondsPerBeat * 1000 + 80);
    });
  }

  private clickMetronome(): Metronome {
    if (this.metronome === null) {
      const context = this.host.context();
      if (context === null) throw new Error('Audio is not running, so the count-in cannot be heard.');
      this.metronome = new Metronome(context);
    }
    return this.metronome;
  }

  private cancelCountIn(): void {
    if (this.countInTimer !== null) {
      clearTimeout(this.countInTimer);
      this.countInTimer = null;
    }
    this.countInCancelled = true;
    this.countingInSignal.value = 0;
    const resolve = this.countInResolver;
    this.countInResolver = null;
    resolve?.();
  }

  // MARK: Monitoring

  /**
   * Turns live monitoring on or off.
   *
   * It starts off and stays off unless the caller passes `headphonesConfirmed`. The web
   * has no way to ask which output device is in use — `enumerateDevices` reports no audio
   * outputs at all on iOS, and a label is not a route — so PadLab does not pretend to
   * know. Instead it defaults to safe, makes the user confirm, and then *watches the
   * result*: the feedback detector mutes monitoring the moment the input starts behaving
   * like an acoustic loop, whatever the user believed about their headphones.
   */
  setMonitoring(enabled: boolean, options: MonitoringOptions = {}): boolean {
    if (!enabled) {
      this.applyMonitorGain(0);
      this.monitoringSignal.value = false;
      return false;
    }
    if (options.headphonesConfirmed === true) this.headphonesConfirmed = true;
    if (!this.headphonesConfirmed) {
      this.noticeSignal.value = MONITORING_WARNING;
      this.monitoringSignal.value = false;
      return false;
    }
    if (this.monitorGain === null) {
      this.noticeSignal.value = 'Open the microphone first, then turn monitoring on.';
      return false;
    }
    this.resetFeedbackDetector();
    this.applyMonitorGain(1);
    this.monitoringSignal.value = true;
    this.noticeSignal.value = null;
    return true;
  }

  /**
   * Inserts an effect chain into the monitor path, so a preset is heard *while* recording.
   *
   * Monitoring was a bare `GainNode`: the selected voice preset never reached the headphones,
   * and `effectChainSupportsLiveMonitoring` — which exists precisely to answer "can this be
   * heard live" — was called from nowhere in the app. Chains containing an offline-only
   * effect (time-stretch, auto-tune, glitch) are refused rather than half-applied, and the
   * caller is told which, because a monitor that quietly drops half the chain is worse than
   * one that says it cannot do it.
   */
  setMonitorChain(chain: EffectChain | null): boolean {
    const context = this.host.context();
    const analyser = this.analyser;
    const monitor = this.monitorGain;
    if (context === null || analyser === null || monitor === null) return false;

    const teardown = (): void => {
      this.monitorChain?.dispose();
      this.monitorChain = null;
    };

    try {
      analyser.disconnect();
    } catch {
      // Not connected yet; the rebuild below is still correct.
    }
    teardown();

    if (chain === null || effectChainActiveNodes(chain).length === 0) {
      analyser.connect(monitor);
      return true;
    }
    if (!effectChainSupportsLiveMonitoring(chain)) {
      analyser.connect(monitor);
      this.noticeSignal.value =
        'That voice uses an effect that can only be applied after recording, so monitoring plays your dry voice. The take itself gets the full effect.';
      return false;
    }

    const graph = buildEffectChain(context, chain);
    analyser.connect(graph.input);
    graph.output.connect(monitor);
    this.monitorChain = graph;
    return true;
  }

  private applyMonitorGain(value: number): void {
    const gain = this.monitorGain;
    if (gain === null) return;
    const context = this.host.context();
    const now = context?.currentTime ?? 0;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    // 8 ms is fast enough to kill a howl before it is loud and slow enough not to click.
    gain.gain.linearRampToValueAtTime(value, now + 0.008);
  }

  private resetFeedbackDetector(): void {
    this.envelope.length = 0;
    this.growthStrikes = 0;
    this.ceilingFrames = 0;
  }

  private muteForFeedback(reason: string): void {
    this.applyMonitorGain(0);
    this.monitoringSignal.value = false;
    this.headphonesConfirmed = false;
    this.resetFeedbackDetector();
    this.noticeSignal.value = reason;
    log.warn('monitoring muted by the feedback guard');
  }

  /** Returns true when the envelope is behaving like an acoustic loop. */
  private detectFeedback(peak: number, rms: number): string | null {
    this.envelope.push(rms);
    if (this.envelope.length > ENVELOPE_HISTORY) this.envelope.shift();

    if (peak >= FEEDBACK_CEILING_LEVEL) {
      this.ceilingFrames += 1;
      if (this.ceilingFrames >= FEEDBACK_CEILING_FRAMES) {
        return 'Monitoring was switched off: the microphone is picking up the output. Use headphones, then turn it back on.';
      }
    } else {
      this.ceilingFrames = 0;
    }

    if (this.envelope.length >= 12) {
      const recent = mean(this.envelope, this.envelope.length - 6, this.envelope.length);
      const previous = mean(this.envelope, this.envelope.length - 12, this.envelope.length - 6);
      if (recent > FEEDBACK_GROWTH_FLOOR && previous > 1e-5 && recent / previous > FEEDBACK_GROWTH_RATIO) {
        this.growthStrikes += 1;
        if (this.growthStrikes >= FEEDBACK_GROWTH_STRIKES) {
          return 'Monitoring was switched off: the level was running away, which means the microphone can hear the output. Headphones are required.';
        }
      } else {
        this.growthStrikes = 0;
      }
    }

    if (this.envelope.length === ENVELOPE_HISTORY && rms > FEEDBACK_CORRELATION_FLOOR) {
      if (envelopeSelfSimilarity(this.envelope) > FEEDBACK_CORRELATION) {
        return 'Monitoring was switched off: the sound is looping back into the microphone. Use headphones, then turn it back on.';
      }
    }
    return null;
  }

  // MARK: Metering

  private startMetering(): void {
    if (this.meterRunning) return;
    this.meterRunning = true;
    this.meterClock.start(() => this.sampleLevel());
  }

  private stopMetering(): void {
    if (!this.meterRunning) return;
    this.meterRunning = false;
    this.meterClock.stop();
  }

  private sampleLevel(): void {
    const analyser = this.analyser;
    if (analyser === null) return;
    analyser.getFloatTimeDomainData(this.analysisBuffer);

    let peak = 0;
    let sum = 0;
    for (let index = 0; index < this.analysisBuffer.length; index++) {
      const value = this.analysisBuffer[index]!;
      const magnitude = Math.abs(value);
      if (magnitude > peak) peak = magnitude;
      sum += value * value;
    }
    const rms = Math.sqrt(sum / this.analysisBuffer.length);
    this.levelSignal.value = { peak, rms };

    if (peak >= CLIP_THRESHOLD) this.latchClip();

    if (this.capturingSignal.peek() && !this.pausedSignal.peek()) {
      this.elapsedSignal.value = this.accumulatedElapsed + Math.max(0, this.host.now() - this.segmentStart);
    }

    if (this.monitoringSignal.peek()) {
      const reason = this.detectFeedback(peak, rms);
      if (reason !== null) this.muteForFeedback(reason);
    }
  }

  private latchClip(): void {
    this.clipSignal.value = true;
    if (this.clipTimer !== null) clearTimeout(this.clipTimer);
    this.clipTimer = setTimeout(() => {
      this.clipSignal.value = false;
      this.clipTimer = null;
    }, CLIP_LATCH_MS);
  }

  private clearClip(): void {
    if (this.clipTimer !== null) {
      clearTimeout(this.clipTimer);
      this.clipTimer = null;
    }
    this.clipSignal.value = false;
  }

  // MARK: Teardown

  dispose(): void {
    this.disposed = true;
    this.cancelCountIn();
    this.abort({ rescue: true });
    this.clearClip();
    this.meterClock.dispose();
    this.metronome?.dispose();
    this.metronome = null;
    this.encoderWorker?.terminate();
    this.encoderWorker = null;
    if (this.encoderWorkerURL !== null) {
      URL.revokeObjectURL(this.encoderWorkerURL);
      this.encoderWorkerURL = null;
    }
    if (this.workletModuleURL !== null) {
      URL.revokeObjectURL(this.workletModuleURL);
      this.workletModuleURL = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.releaseOnHide);
      window.removeEventListener('beforeunload', this.releaseOnHide);
    }
  }
}

// MARK: - Helpers

function isRealtimeContext(context: BaseAudioContext): context is AudioContext {
  return typeof (context as AudioContext).createMediaStreamSource === 'function';
}

function mean(values: readonly number[], from: number, to: number): number {
  let sum = 0;
  for (let index = from; index < to; index++) sum += values[index] ?? 0;
  return sum / Math.max(1, to - from);
}

/**
 * Highest normalised autocorrelation of the envelope at any lag between 3 and 20 frames
 * (100–660 ms at the 30 Hz metering rate). Speech and music vary; a howl repeats.
 */
function envelopeSelfSimilarity(values: readonly number[]): number {
  const length = values.length;
  let average = 0;
  for (const value of values) average += value;
  average /= length;

  let variance = 0;
  for (const value of values) variance += (value - average) * (value - average);
  if (variance < 1e-9) return 0;

  let best = 0;
  for (let lag = 3; lag <= Math.min(20, length - 8); lag++) {
    let correlation = 0;
    for (let index = lag; index < length; index++) {
      correlation += (values[index]! - average) * (values[index - lag]! - average);
    }
    const normalised = correlation / variance;
    if (normalised > best) best = normalised;
  }
  return best;
}
